import React from 'react';
import { useSelector } from 'react-redux';
import { selectAuthRefreshTokenExpiresAt } from 'src/redux/auth/selectors';
import { AuthToken } from './types';
import { Auth } from 'aws-amplify';

interface AuthTokenResult {
  /** The user's current auth token, if authenticated. */
  value?: AuthToken;
  /** Whether the session is expired. If undefined, no session exists. */
  isExpired?: boolean;
  /** Whether the user is authenticated, regardless of whether the session is valid. */
  isAuthenticated: boolean;
}

/**
 * A hook that returns the guest user's current auth tokens (access and refresh), if the user is authenticated.
 *
 * Adapted from useAuthToken().
 */
export const useGuestAuthToken = (): AuthTokenResult => {
  const [currentDateTime, setCurrentDateTime] = React.useState(Date.now());
  const [cognitoAccessToken, setCognitoAccessToken] = React.useState<string | undefined>(undefined);
  const [cognitoAccessTokenExp, setCognitoAccessTokenExp] = React.useState<number | undefined>(undefined);
  const [cognitoAccessTokenIssuedAt, setCognitoAccessTokenIssuedAt] = React.useState<number | undefined>(undefined);
  const [cognitoRefreshToken, setCognitoRefreshToken] = React.useState<string | undefined>(undefined);
  const refreshExp = useSelector(selectAuthRefreshTokenExpiresAt);

  // An effect that causes the current datetime to update on an interval, so the `isExpired` flag can be recomputed.
  React.useEffect(() => {
    const intervalHandle = window.setInterval(() => {
      setCurrentDateTime(Date.now());
    }, 1000 * 60);

    return () => {
      window.clearInterval(intervalHandle);
    };
  }, []);

  // The Authenticator is not used for a guest, so we can't use useAuthenticator()
  // like useAuthToken().
  Auth.currentSession().then(session => {
    const accessToken = session?.getAccessToken();
    setCognitoAccessToken(accessToken.getJwtToken());
    setCognitoAccessTokenExp(accessToken?.getExpiration());
    setCognitoAccessTokenIssuedAt(accessToken?.getIssuedAt());
    setCognitoRefreshToken(session.getRefreshToken().getToken());
  });

  const value = React.useMemo(() => {
    if (
      cognitoAccessToken === undefined ||
      cognitoAccessTokenExp === undefined ||
      cognitoRefreshToken === undefined ||
      cognitoAccessTokenIssuedAt === undefined
    ) {
      return undefined;
    }

    return {
      access: {
        token: cognitoAccessToken,
        expiresAt: cognitoAccessTokenExp * 1000,
        issuedAt: cognitoAccessTokenIssuedAt * 1000,
      },
      refresh: { token: cognitoRefreshToken },
    };
  }, [cognitoAccessToken, cognitoAccessTokenExp, cognitoAccessTokenIssuedAt, cognitoRefreshToken]);

  const isAuthenticated = React.useMemo<boolean>(() => {
    if (refreshExp === undefined) return false;
    return currentDateTime < refreshExp;
  }, [currentDateTime, refreshExp]);

  const isExpired = React.useMemo<boolean | undefined>(() => {
    if (!isAuthenticated) return undefined;
    if (cognitoAccessTokenExp === undefined) return true;
    return cognitoAccessTokenExp * 1000 < currentDateTime;
  }, [cognitoAccessTokenExp, currentDateTime, isAuthenticated]);

  // returning the value from useMemo avoids redundant variable waring.
  return React.useMemo<AuthTokenResult>(() => {
    const _result: AuthTokenResult = { value, isExpired, isAuthenticated };
    return _result;
  }, [isAuthenticated, isExpired, value]);
};
